import "../landing.css";
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/contexts/AuthContext";
import {
  formatCurrency,
  formatDate,
  formatDateTime,
  cn,
  getMediaUrl,
} from "@/lib/utils";
import {
  Trophy,
  Calendar,
  Clock,
  Star,
  Sparkles,
  Award,
  Users,
  ChevronRight,
  ArrowUpRight,
} from "lucide-react";
import Countdown from "../components/Countdown";
import LiveAnimation from "../components/LiveAnimation";
import CampaignSection from "../components/CampaignSection";
import FastCheckoutModal from "../components/FastCheckoutModal";
import WaitingSorteoSection from "../components/WaitingSorteoSection";
import SocialProof from "../components/SocialProof";
import websocketService from "../services/websocket";
import confetti from "canvas-confetti";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// URLs de imágenes
const HERO_IMAGE =
  "https://customer-assets.emergentagent.com/job_rafflehub-1/artifacts/b5g73ddt_image.png";
const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_rafflehub-1/artifacts/13c8161p_descarga.png";

const HomeComplete = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [sorteosLive, setSorteosLive] = useState([]);
  const [sorteosWaiting, setSorteosWaiting] = useState([]);
  const [sorteosPublished, setSorteosPublished] = useState([]);
  const [sorteosCompleted, setSorteosCompleted] = useState([]);
  const [ganadores, setGanadores] = useState([]);
  const [loading, setLoading] = useState(true);
  const [countdowns, setCountdowns] = useState({});
  const [liveAnimations, setLiveAnimations] = useState({});
  const [liveParticipants, setLiveParticipants] = useState({}); // Participantes por sorteo
  const [heroConfig, setHeroConfig] = useState(null);
  const [branding, setBranding] = useState({ logo_url: LOGO_URL });
  const [openFaq, setOpenFaq] = useState(null);
  const [isFastCheckoutOpen, setIsFastCheckoutOpen] = useState(false);
  const [selectedPackageId, setSelectedPackageId] = useState(null);
  const [selectedPackageData, setSelectedPackageData] = useState(null);

  useEffect(() => {
    fetchAllData();
    fetchHeroConfig();
    fetchBranding();
    // ... REST OF USEEFFECT ...

    // Conectar WebSocket
    websocketService.connect();

    // Escuchar actualización global de sorteos
    const handleSorteosListUpdated = () => {
      console.log("📡 Lista de sorteos actualizada desde WebSocket");
      fetchAllData();
    };

    websocketService.onSorteosListUpdated(handleSorteosListUpdated);

    // Escuchar actualización de branding
    websocketService.on("branding_updated", (data) => {
      console.log("Branding actualizado via WebSocket:", data);
      if (data.logo_url) {
        // Normalizar URL y añadir timestamp para evitar cache
        const normalizedUrl = getMediaUrl(data.logo_url);
        const freshUrl = `${normalizedUrl}${normalizedUrl.includes("?") ? "&" : "?"}t=${Date.now()}`;
        setBranding((prev) => ({ ...prev, logo_url: freshUrl }));
      }
    });

    // Polling para actualizar estados automáticamente cada 60 segundos
    const pollingInterval = setInterval(async () => {
      try {
        // Llamar al backend para actualizar estados
        await axios.post(`${API}/admin/actualizar-estados-sorteos`);
        // Recargar datos
        fetchAllData();
      } catch (error) {
        console.error("Error al actualizar estados:", error);
      }
    }, 60000); // Cada 60 segundos

    // Limpieza automática de sorteos antiguos cada 24 horas
    const limpiezaInterval = setInterval(async () => {
      try {
        await axios.post(`${API}/admin/limpiar-sorteos-antiguos`);
        console.log("Limpieza de sorteos antiguos ejecutada");
      } catch (error) {
        console.error("Error en limpieza automática:", error);
      }
    }, 86400000); // Cada 24 horas

    return () => {
      clearInterval(pollingInterval);
      clearInterval(limpiezaInterval);
      websocketService.offSorteosListUpdated(handleSorteosListUpdated);
    };
  }, []);

    // Track ViewContent for the main raffle
    useEffect(() => {
      if (window.fbq) {
        window.fbq('track', 'ViewContent', {
          content_name: 'WishWay BMW 2JZ',
          content_category: 'Sorteo Principal',
          currency: 'USD'
        });
      }
    }, []);

  const fetchAllData = async () => {
    try {
      const [sorteosRes, ganadoresRes] = await Promise.all([
        axios.get(`${API}/sorteos`),
        axios.get(`${API}/ganadores/recientes`),
      ]);

      const allSorteos = (sorteosRes.data || []).filter(
        (s) => s.oculto !== true,
      );
      const ahora = new Date();

      // Filtrar sorteos por estado - REGLA MAESTRA DE EXCLUSIÓN
      const live = allSorteos.filter((s) => s.estado === "live");

      // En espera incluye tanto 'waiting' (5 min final) como 'ready' (cierre manual)
      const waiting = allSorteos.filter(
        (s) => s.estado === "waiting" || s.estado === "ready",
      );

      // Publicados: SOLO si están en venta y NO están en proceso de sorteo (vivos) o finalizados
      const published = allSorteos.filter(
        (s) =>
          (s.estado === "published" ||
            s.estado === "activo" ||
            s.estado === "pausado") &&
          s.oculto !== true &&
          s.estado !== "live" &&
          s.estado !== "waiting" &&
          s.estado !== "ready" &&
          s.estado !== "completed" &&
          s.estado !== "completado",
      );

      const completed = allSorteos.filter(
        (s) => s.estado === "completed" || s.estado === "completado",
      );

      setSorteosLive(live);
      setSorteosWaiting(waiting);
      setSorteosPublished(published);
      setSorteosCompleted(completed);
      setGanadores(ganadoresRes.data);

      // Cargar participantes para sorteos LIVE
      live.forEach((sorteo) => {
        if (!liveParticipants[sorteo.id]) {
          loadParticipants(sorteo.id);
        }
      });
    } catch (error) {
      console.error("Error al cargar datos:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadParticipants = async (sorteoId) => {
    try {
      const response = await axios.get(
        `${API}/sorteos/${sorteoId}/participantes`,
      );
      setLiveParticipants((prev) => ({
        ...prev,
        [sorteoId]: response.data.participantes || [],
      }));
    } catch (error) {
      console.error("Error al cargar participantes:", error);
    }
  };

  const fetchHeroConfig = async () => {
    try {
      const res = await axios.get(`${API}/hero-home`);
      setHeroConfig(res.data);
    } catch (error) {
      console.error("Error al cargar config hero:", error);
    }
  };

  const fetchBranding = async () => {
    try {
      const response = await axios.get(`${API}/settings/branding`);
      if (response.data && response.data.logo_url) {
        setBranding({
          ...response.data,
          logo_url: getMediaUrl(response.data.logo_url),
        });
      }
    } catch (error) {
      console.error("Error al cargar branding:", error);
    }
  };

  // --- LOCAL CONFETTI FOR WINNER WALL ---
  useEffect(() => {
    const canvas = document.getElementById("winner-wall-confetti");
    if (!canvas || ganadores.length === 0) return;

    const myConfetti = confetti.create(canvas, {
      resize: true,
      useWorker: true,
    });

    const duration = 15 * 60 * 1000;
    const animationEnd = Date.now() + duration;

    function randomInRange(min, max) {
      return Math.random() * (max - min) + min;
    }

    const interval = setInterval(function () {
      const timeLeft = animationEnd - Date.now();
      if (timeLeft <= 0) return clearInterval(interval);

      myConfetti({
        particleCount: 15,
        startVelocity: 30,
        spread: 360,
        origin: { x: randomInRange(0.1, 0.9), y: randomInRange(0.1, 0.9) },
        colors: ["#EAB308", "#F59E0B", "#F97316", "#FFFFFF"],
      });
    }, 1500);

    return () => clearInterval(interval);
  }, [ganadores]);

  const getYouTubeID = (url) => {
    if (!url) return "";
    try {
      // 1. Intentar con URL object para mayor precisión
      const urlObj = new URL(url.startsWith("http") ? url : `https://${url}`);
      if (urlObj.hostname.includes("youtube.com")) {
        if (urlObj.pathname === "/watch")
          return urlObj.searchParams.get("v") || "";
        if (urlObj.pathname.startsWith("/embed/"))
          return urlObj.pathname.split("/")[2];
        if (urlObj.pathname.startsWith("/shorts/"))
          return urlObj.pathname.split("/")[2];
        if (urlObj.pathname.startsWith("/live/"))
          return urlObj.pathname.split("/")[2];
      }
      if (urlObj.hostname === "youtu.be") return urlObj.pathname.slice(1);
    } catch (e) {
      // Fallback regex si URL falla
    }

    const regExp =
      /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?v=)|(\&v=)|(shorts\/)|(live\/))([^#\&\?]*).*/;
    const match = url.match(regExp);
    if (match && match[10]) return match[10];

    if (url.length >= 10 && !url.includes("/") && !url.includes("."))
      return url;
    return "";
  };

  const handleAnimationComplete = async (sorteoId, winners) => {
    try {
      // Marcar sorteo como COMPLETED
      await axios.put(`${API}/admin/sorteo/${sorteoId}/completar`, {});
      console.log("Sorteo marcado como COMPLETED");

      // Recargar datos inmediatamente para mostrar en "Completados"
      fetchAllData();
    } catch (error) {
      console.error("Error al completar sorteo:", error);
      // Recargar de todas formas
      fetchAllData();
    }
  };

  const updateCountdowns = () => {
    const newCountdowns = {};
    sorteosWaiting.forEach((sorteo) => {
      const ahora = new Date();

      // Si tiene waiting_hasta, usar ese (contador de 5 min)
      if (sorteo.waiting_hasta) {
        const waitingHasta = new Date(sorteo.waiting_hasta);
        const diff = waitingHasta - ahora;

        if (diff > 0) {
          const minutos = Math.floor(diff / (1000 * 60));
          const segundos = Math.floor((diff % (1000 * 60)) / 1000);
          newCountdowns[sorteo.id] = {
            horas: 0,
            minutos,
            segundos,
            usaWaitingHasta: true,
          };
        } else {
          newCountdowns[sorteo.id] = {
            horas: 0,
            minutos: 0,
            segundos: 0,
            usaWaitingHasta: true,
          };
        }
      } else {
        // Fallback: usar fecha_cierre
        const fechaInicio = new Date(sorteo.fecha_cierre);
        const diff = fechaInicio - ahora;

        if (diff > 0) {
          const horas = Math.floor(diff / (1000 * 60 * 60));
          const minutos = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const segundos = Math.floor((diff % (1000 * 60)) / 1000);
          newCountdowns[sorteo.id] = {
            horas,
            minutos,
            segundos,
            usaWaitingHasta: false,
          };
        }
      }
    });
    setCountdowns(newCountdowns);
  };

  // Lógica para paquetes del sorteo activo
  const sorteoActivo = sorteosLive.length > 0 ? sorteosLive[0] : (sorteosPublished.length > 0 ? sorteosPublished[0] : (sorteosWaiting.length > 0 ? sorteosWaiting[0] : null));
  const metaPaquetes = React.useMemo(() => {
    if (!sorteoActivo) return [];
    
    // Si no hay paquetes configurados, inyectar paquetes por defecto (fallback)
    let paquetesBase = sorteoActivo.paquetes || [];
    if (paquetesBase.length === 0) {
      paquetesBase = [
        { id: "fallback-10", cantidad: 10, boletos_gratis: 0, descuento: 0, activo: true },
        { id: "fallback-30", cantidad: 30, boletos_gratis: 5, descuento: 0, activo: true },
        { id: "fallback-50", cantidad: 50, boletos_gratis: 10, descuento: 0, activo: true }
      ];
    }
    
    // Enriquecer datos
    const enriched = paquetesBase.map(p => {
      const descuentoSub = (p.descuento || 0) / 100;
      const totalTickets = p.cantidad + (p.boletos_gratis || 0);
      const precioNormalVal = totalTickets * sorteoActivo.precio_boleto;
      const precioConDescVal = p.cantidad * sorteoActivo.precio_boleto * (1 - descuentoSub);
      const ahorroTotal = precioNormalVal - precioConDescVal;
      const ratio = precioConDescVal / (totalTickets || 1);
      
      return { 
        ...p, 
        precioNormal: precioNormalVal, 
        precioConDesc: precioConDescVal, 
        totalTickets, 
        ahorroTotal: ahorroTotal > 0 ? ahorroTotal : 0,
        ratio
      };
    });

    const sortedByValue = [...enriched].sort((a, b) => a.ratio - b.ratio);
    const protagonistaId = sortedByValue[0]?.id || enriched[enriched.length - 1]?.id;

    let finalOrder = [...enriched].sort((a, b) => a.cantidad - b.cantidad);
    
    if (finalOrder.length >= 3) {
      const bestIdx = finalOrder.findIndex(p => p.id === protagonistaId);
      if (bestIdx !== -1) {
        const best = finalOrder.splice(bestIdx, 1)[0];
        finalOrder.splice(Math.floor(finalOrder.length / 2), 0, best);
      }
    }

    return finalOrder.map(p => ({ ...p, isProtagonista: p.id === protagonistaId }));
  }, [sorteoActivo]);

  React.useEffect(() => {
    if (loading) return;
    
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('active');
        }
      });
    }, { threshold: 0.1 });

    const hiddenElements = document.querySelectorAll('.reveal, .reveal-left, .reveal-right, .reveal-up, .reveal-car-scale');
    hiddenElements.forEach((el) => observer.observe(el));

    return () => {
      hiddenElements.forEach((el) => observer.unobserve(el));
      observer.disconnect();
    };
  }, [loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="space-y-4 text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto" />
          <p className="text-white/60 font-black uppercase tracking-[0.3em] text-[10px]">
            Cargando Experiencia...
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
<div className="bg-brand-dark text-gray-200 font-sans selection:bg-brand-primary selection:text-white">

    

    {/*  TOP PROGRESS BAR COMPONENT  */}
    <aside className="w-full bg-black relative z-40 border-b border-white/10 group cursor-default">
        <div
            className="relative h-8 md:h-10 w-full bg-black overflow-hidden shadow-[0_4px_20px_rgba(255,106,1,0.3)] group-hover:shadow-[0_4px_35px_rgba(255,106,1,0.6)] transition-all duration-500">

            {/*  Franja Anaranjada con Degradado Extremo  */}
            <div
                className="absolute inset-0 bg-gradient-to-r from-[#803100] via-[#FF8B3D] to-[#803100] flex items-center justify-center animate-fillBar">

                {/*  Overlay de Brillo para resaltar el centro  */}
                <div
                    className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.2),transparent_70%)]">
                </div>

                {/*  Destello de Luz Central Máximo (Highlight)  */}
                <div className="absolute top-0 left-0 w-full h-[1.5px] bg-white/40"></div>
                <div className="absolute -top-[2px] left-1/2 -translate-x-1/2 w-2/3 h-[3px] bg-white blur-[1.2px]"></div>
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-1/2 h-8 bg-[#FF6A01]/30 blur-2xl rounded-full">
                </div>
            </div>

            {/*  Texto Centrado  */}
            <div className="absolute inset-0 flex items-center justify-center">
                <p
                    className="text-[10px] md:text-[13px] font-black uppercase tracking-[0.35em] text-[#000] drop-shadow-[0_1px_0_rgba(255,255,255,0.3)] pointer-events-none">
                    {heroConfig?.progreso_falso || 35}% <span className="font-bold">Vendido</span>
                </p>
            </div>
        </div>
    </aside>

    {/*  MAIN CONTENT WRAPPER  */}
    <main className="relative min-h-screen">
        {/*  HERO SCENE (FONDO + CARRO ANIMADO ELITE)  */}
        <section className="relative min-h-[85vh] md:min-h-[90vh] flex items-center overflow-hidden bg-black">

            {/*  1. CAPA DE FONDO (ESTÁTICA)  */}
            <div className="absolute inset-0 z-0">
                <img src="assets/fondosincarro.png" alt="City Stage"
                    className="w-full h-full object-cover brightness-[1.1] contrast-[1.1] object-center" />
                {/*  Overlay sutil  */}
                <div
                    className="absolute inset-0 bg-gradient-to-r from-black via-black/60 to-transparent lg:from-black/95 lg:via-black/30 lg:to-transparent">
                </div>
            </div>

            {/*  2. CAPA DEL CARRO (ANIMACIÓN ELITE REVEAL)  */}
            <div className="absolute inset-0 z-10 pointer-events-none">
                <div className="container-main relative h-full w-full">
                    {/*  Tamaño Profesional Imponente - Posición Extrema: más abajo y más a la derecha  */}
                    <div
                        className="absolute bottom-[2%] md:bottom-[5%] right-[-45%] md:right-[-25%] lg:right-[-22%] w-[135%] md:w-[105%] lg:w-[95%] animate-car-entry">
                        {/*  Sombra dinámica  */}
                        <div
                            className="absolute bottom-[4%] left-[45%] -translate-x-1/2 w-[75%] h-14 bg-black/70 blur-3xl rounded-full animate-shadow-in">
                        </div>
                        {/*  Imagen del BMW  */}
                        <img src="assets/carrosinfondo.png" alt="BMW"
                            className="w-full h-auto object-contain drop-shadow-[0_45px_45px_rgba(0,0,0,0.4)]" />
                    </div>
                </div>
            </div>

            {/*  3. CAPA DE CONTENIDO (DELANTE)  */}
            <div className="container-main relative z-20 w-full py-16">
                <div
                    className="max-w-4xl reveal flex flex-col items-center text-center lg:items-start lg:text-left mx-auto lg:ml-0">

                    {/*  Logo Principal  */}
                    <img src="assets/logo.png" alt="WishWay Logo"
                        className="h-16 md:h-24 w-auto mb-6 md:mb-8 object-contain drop-shadow-md" />

                    <h1
                        className="text-2xl md:text-5xl lg:text-6xl font-black text-white leading-tight mb-6 tracking-tight drop-shadow-lg whitespace-nowrap overflow-hidden border-r-4 border-brand-primary pr-2 animate-typing mx-auto lg:mx-0">
                        Gana este BMW de $40.000
                    </h1>

                    

                    <p
                        className="text-sm md:text-xl text-gray-200 mb-10 leading-relaxed font-light max-w-sm md:max-w-lg drop-shadow-md">
                        Participa de forma segura, transparente y con oportunidades reales.
                    </p>

                    <div className="flex flex-col gap-4 mb-12 w-full max-w-[280px] md:max-w-[340px]">
                        <button onClick={() => document.getElementById('participate')?.scrollIntoView({ behavior: 'smooth' })}
                            className="flex items-center justify-center w-full py-4 md:py-5 rounded-lg bg-gradient-to-b from-[#FF8A00] to-[#E65100] text-white font-bold text-base md:text-lg shadow-lg active:scale-95 transition-all duration-500 uppercase tracking-wide relative overflow-hidden group">
                            <span className="relative z-10">PARTICIPAR AHORA</span>
                            <div
                                className="absolute inset-0 w-1/2 h-full bg-white/10 -skew-x-[45deg] -translate-x-full animate-shine pointer-events-none">
                            </div>
                        </button>
                        <button 
                            onClick={() => navigate(user ? (user.role === 'admin' ? '/admin' : (user.role === 'vendedor' ? '/vendedor' : '/usuario')) : '/login')}
                            className="flex items-center justify-center gap-2 w-full py-3 rounded-lg border border-white/20 bg-black/20 text-white/90 hover:bg-white/10 hover:text-white font-semibold text-sm transition-all duration-300"
                        >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
                            </svg>
                            {user?.role === 'admin' ? 'Panel de Admin' : (user?.role === 'vendedor' ? 'Panel de Vendedor' : 'Ver mis boletos')}
                        </button>
                    </div>

                    <div className="bg-black/70 backdrop-blur-md p-5 rounded-2xl border border-white/10 inline-block">
                        <ul className="space-y-3 flex flex-col items-center lg:items-start text-left">
                            <li className="flex items-center gap-4 text-white/90 group">
                                <div
                                    className="flex-shrink-0 w-5 h-5 flex items-center justify-center border border-white/20 rounded-full">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4"
                                            d="M5 13l4 4L19 7"></path>
                                    </svg>
                                </div>
                                <span className="text-xs md:text-lg font-medium tracking-wide">Proceso transparente</span>
                            </li>
                            <li className="flex items-center gap-4 text-white/90 group">
                                <div
                                    className="flex-shrink-0 w-5 h-5 flex items-center justify-center border border-white/20 rounded-full">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4"
                                            d="M5 13l4 4L19 7"></path>
                                    </svg>
                                </div>
                                <span className="text-xs md:text-lg font-medium tracking-wide">Resultado verificable</span>
                            </li>
                            <li className="flex items-center gap-4 text-white/90 group">
                                <div
                                    className="flex-shrink-0 w-5 h-5 flex items-center justify-center border border-white/20 rounded-full">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor"
                                        viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="4"
                                            d="M5 13l4 4L19 7"></path>
                                    </svg>
                                </div>
                                <span className="text-xs md:text-lg font-medium tracking-wide">Participación segura</span>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </section>

        {/*  SECCIÓN GALERÍA DEL PREMIO (DISEÑO PREMIUM CLARO)  */}
        
      {/* SECCIÓN 2: SORTEOS EN VIVO (LIVE) */}
      {sorteosLive.length > 0 && (
        <div className="bg-gradient-to-b from-black via-red-900/40 to-black py-8 px-4 border-b border-red-500/20 relative z-50">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-6">
              <Badge className="bg-gradient-to-r from-red-600 to-red-800 text-white text-md px-6 py-2 mb-4 animate-pulse shadow-[0_0_15px_rgba(220,38,38,0.5)] border border-red-400">
                🔴 SORTEO EN VIVO
              </Badge>
              <h2 className="text-4xl lg:text-5xl font-bold mb-2 text-white">
                Transmisión en Directo
              </h2>
              <p className="text-lg text-gray-300">¡El sorteo está sucediendo ahora!</p>
            </div>

            <div className="space-y-8">
              {sorteosLive.map((sorteo) => {
                const participantes = liveParticipants[sorteo.id] || [];
                return (
                  <LiveAnimation
                    key={sorteo.id}
                    sorteo={sorteo}
                    user={user}
                  />
                );
              })}
            </div>
          </div>
        </div>
      )}

<section id="gallery" className="py-20 bg-white overflow-hidden relative border-t border-gray-100">
            <div className="container-main">

                {/*  Título de la Galería  */}
                <div className="text-center mb-16 reveal">
                    <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-gray-900 tracking-tight">
                        Un premio real, listo para su próximo dueño.
                    </h2>
                    <div className="w-20 h-1 bg-brand-primary/40 mx-auto mt-6 rounded-full"></div>
                </div>

                {/*  Grid de Imágenes Dinámico  */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">

                    {/*  Imagen 1  */}
                    <div className="reveal group h-full">
                        <div
                            className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-sm bg-white border border-gray-100 transition-all duration-700 hover:shadow-[0_35px_70px_-15px_rgba(0,0,0,0.3)] hover:-translate-y-2 cursor-zoom-in lightbox-trigger">
                            <img src="assets/car1.jpeg" alt="BMW Vista 1"
                                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                            <div
                                className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors duration-500">
                            </div>
                        </div>
                    </div>

                    {/*  Imagen 2  */}
                    <div className="reveal group h-full delay-100">
                        <div
                            className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-sm bg-white border border-gray-100 transition-all duration-700 hover:shadow-[0_35px_70px_-15px_rgba(0,0,0,0.3)] hover:-translate-y-2 cursor-zoom-in lightbox-trigger">
                            <img src="assets/car2.jpeg" alt="BMW Vista 2"
                                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                            <div
                                className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors duration-500">
                            </div>
                        </div>
                    </div>

                    {/*  Imagen 3  */}
                    <div className="reveal group h-full delay-200">
                        <div
                            className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-sm bg-white border border-gray-100 transition-all duration-700 hover:shadow-[0_35px_70px_-15px_rgba(0,0,0,0.3)] hover:-translate-y-2 cursor-zoom-in lightbox-trigger">
                            <img src="assets/car3.jpeg" alt="BMW Vista 3"
                                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                            <div
                                className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors duration-500">
                            </div>
                        </div>
                    </div>

                    {/*  Imagen 4  */}
                    <div className="reveal group h-full delay-300">
                        <div
                            className="relative aspect-[4/3] rounded-2xl overflow-hidden shadow-sm bg-white border border-gray-100 transition-all duration-700 hover:shadow-[0_35px_70px_-15px_rgba(0,0,0,0.3)] hover:-translate-y-2 cursor-zoom-in lightbox-trigger">
                            <img src="assets/car4.jpeg" alt="BMW Vista 4"
                                className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-110" />
                            <div
                                className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors duration-500">
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>

        {/*  SECCIÓN ¿CÓMO QUIERES PARTICIPAR? (RÉPLICA REFINADA)  */}
        
        {/* SECCIÓN DE PARTICIPACIÓN DINÁMICA */}
        {sorteoActivo && (
        <>
        <section id="participate" className="w-full bg-gray-50 py-12 md:py-24 relative overflow-visible z-30">
            <div className="absolute top-0 left-0 w-full h-24 bg-gradient-to-b from-white to-transparent"></div>
            
            <div className="container-main relative z-10">
                <div className="text-center mb-10 md:mb-16 reveal relative">
                    <h2 className="text-3xl md:text-5xl font-black tracking-tighter text-[#1a2233]">
                        ¿Cómo quieres <span className="text-[#f24e00]">participar?</span>
                    </h2>
                    <p className="mt-3 text-gray-500 font-bold text-sm md:text-lg tracking-wide uppercase">
                        Elige tu nivel de participación en {sorteoActivo.titulo}
                    </p>
                </div>

                <div className="flex flex-col lg:flex-row flex-wrap items-center justify-center gap-8 lg:gap-10 max-w-7xl mx-auto px-4 md:px-0 w-full">
                    {metaPaquetes.map((p, index) => {
                        // Estilos basados en la tarjeta original
                        const isPro = p.isProtagonista;
                        const cardClass = isPro 
                            ? "w-full max-w-sm lg:max-w-none lg:w-[280px] xl:w-[300px] relative rounded-[22px] bg-gradient-to-b from-[#f57516] via-[#df4b02] to-[#b33300] shadow-[0_30px_60px_rgba(223,75,2,0.4)] p-6 pt-12 pb-6 flex flex-col items-center text-center h-[380px] lg:scale-110 lg:z-20 border-[4px] border-[#a32b00] shadow-[inset_0_2px_6px_rgba(255,255,255,0.4),inset_0_-2px_8px_rgba(0,0,0,0.3)] overflow-hidden transition-all hover:scale-[1.12]"
                            : index === 0 
                            ? "w-full max-w-sm lg:max-w-none lg:w-[280px] xl:w-[300px] relative rounded-[22px] bg-white shadow-[0_20px_40px_rgba(0,0,0,0.08)] p-6 pt-8 pb-6 flex flex-col items-center text-center h-[360px] border border-gray-100 overflow-hidden transition-all hover:shadow-[0_25px_50px_rgba(0,0,0,0.12)] hover:-translate-y-2 lg:z-10"
                            : "w-full max-w-sm lg:max-w-none lg:w-[280px] xl:w-[300px] relative rounded-[22px] bg-gradient-to-b from-[#2a52ab] via-[#102a5c] to-[#081531] shadow-[0_30px_60px_rgba(10,21,49,0.5)] p-6 pt-8 pb-6 flex flex-col items-center text-center h-[360px] border-[4px] border-[#06122b] shadow-[inset_0_2px_6px_rgba(255,255,255,0.3),inset_0_-2px_8px_rgba(0,0,0,0.4)] overflow-hidden transition-all hover:shadow-[0_35px_70px_rgba(10,21,49,0.6)] hover:-translate-y-2 lg:z-10";

                        const textColor = isPro ? "text-white" : index === 0 ? "text-[#1a2233]" : "text-white";
                        const subColor = isPro ? "text-white/90" : index === 0 ? "text-gray-500" : "text-white/90";
                        const pillBg = isPro ? "bg-[#c64000] border-white/20 text-white" : index === 0 ? "bg-gray-100 border-gray-200 text-gray-600" : "bg-[#143473] border-white/30 text-white";
                        const btnClass = isPro 
                            ? "w-full h-[54px] flex items-center justify-center rounded-xl bg-gradient-to-b from-[#fca050] to-[#f24e00] text-[#3f2506] font-black uppercase tracking-widest text-[13px] shadow-[0_6px_0_#b35a00] border-t border-white/40 hover:brightness-110 active:translate-y-1 active:shadow-none transition-all relative overflow-hidden"
                            : index === 0
                            ? "w-full h-[54px] flex items-center justify-center rounded-xl bg-gradient-to-b from-[#1a2233] to-[#0a0f1a] text-white font-black uppercase tracking-widest text-[13px] shadow-[0_6px_0_#000] border-t border-white/20 hover:brightness-125 active:translate-y-1 active:shadow-none transition-all relative overflow-hidden"
                            : "w-full h-[54px] flex items-center justify-center rounded-xl bg-gradient-to-b from-[#3d67c1] to-[#143473] text-white font-black uppercase tracking-widest text-[13px] shadow-[0_6px_0_#0a1b3d] border-t border-white/30 hover:brightness-110 active:translate-y-1 active:shadow-none transition-all relative overflow-hidden";
                            
                        return (
                            <div key={p.id} className={cardClass}>
                                {isPro && (
                                    <>
                                        <div className="absolute top-0 left-0 w-24 h-24 bg-gradient-to-br from-white/40 via-white/10 to-transparent pointer-events-none z-30"></div>
                                        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.25),transparent_70%)] pointer-events-none"></div>
                                        <div className="absolute top-4 bg-[#cc3e00] border border-white/20 text-white text-[9px] font-black px-5 py-1.5 rounded-md uppercase tracking-[0.2em] shadow-lg flex items-center gap-1">
                                            🔥 MÁS ELEGIDO
                                        </div>
                                    </>
                                )}
                                {!isPro && index !== 0 && (
                                    <div className="absolute top-4 bg-[#0d47a1] border border-white/20 text-white text-[9px] font-black px-5 py-1.5 rounded-md uppercase tracking-[0.2em] shadow-lg flex items-center gap-1">
                                        ⚡ Mejor valor
                                    </div>
                                )}

                                <div className="mt-6 mb-4 relative z-10">
                                    <span className={`text-[85px] font-[1000] ${textColor} leading-none tracking-tighter drop-shadow-[0_4px_8px_rgba(0,0,0,0.3)]`}>{p.cantidad}</span>
                                    <div className={`text-[20px] font-black ${textColor} uppercase tracking-[0.2em] -mt-3 drop-shadow-md`}>Boletos</div>
                                </div>

                                <div className={`relative z-10 ${pillBg} border font-black px-8 py-2.5 rounded-2xl text-xl mb-4 shadow-[inset_0_2px_10px_rgba(0,0,0,0.1),0_4px_10px_rgba(0,0,0,0.1)]`}>
                                    {p.boletos_gratis > 0 ? `+ ${p.boletos_gratis} GRATIS` : 'Participación Básica'}
                                </div>

                                <p className={`relative z-10 ${subColor} font-bold text-sm mb-auto`}>Recibes <span className="text-2xl font-black">{p.totalTickets}</span> números</p>

                                <button onClick={() => { 
                                    setSelectedPackageId(p.id); 
                                    setSelectedPackageData(p); 
                                    setIsFastCheckoutOpen(true); 
                                    if (window.fbq) {
                                        const valorPaquete = Number((p.precioConDesc || (p.cantidad * (sorteoActivo?.precio_boleto || 0) * (1 - ((p.descuento || 0) / 100)))).toFixed(2));
                                        const payload = {
                                            content_name: `Paquete ${p.cantidad} boletos`,
                                            content_ids: [p.id],
                                            content_type: 'product',
                                            value: valorPaquete,
                                            currency: 'USD'
                                        };
                                        window.fbq('trackCustom', 'SelectPackage', payload);
                                        window.fbq('trackCustom', 'TicketPackageSelected', payload);
                                        window.fbq('track', 'AddToCart', payload);
                                    }
                                }} className={btnClass}>
                                    <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.3),transparent_60%)] pointer-events-none"></div>
                                    Participar ahora
                                </button>
                            </div>
                        );
                    })}
                </div>

                {/*  Bottom Features  */}
                <div
                    className="mt-10 md:mt-14 flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-12 reveal-up">
                    <div className="flex items-center gap-2 text-sm md:text-base font-bold text-[#1a2233]">
                        <span
                            className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white text-[9px]">✔</span>
                        <span className="uppercase tracking-widest">Pago seguro</span>
                    </div>

                    <div className="flex items-center gap-2 text-sm md:text-base font-bold text-[#1a2233]">
                        <span
                            className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-[#f24e00] text-white text-[9px]">✔</span>
                        <span className="uppercase tracking-widest">Asignación automática</span>
                    </div>

                    <div className="flex items-center gap-2 text-sm md:text-base font-bold text-[#1a2233]">
                        <span
                            className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-blue-600 text-white text-[9px]">✔</span>
                        <span className="uppercase tracking-widest">Resultado verificable</span>
                    </div>
                </div>
            </div>

            {/*  MANTENER LA ANIMACIÓN DEL CARRO (CRÍTICO)  */}
            <div
                className="absolute bottom-2 left-0 w-full md:bottom-[-20px] pointer-events-none z-[5] overflow-hidden leading-[0] drive-by-trigger">
                <img id="drive-by-car" src="/assets/carrosinfondo.png" alt="BMW Drive By"
                    className="w-72 md:w-[520px] h-auto object-contain animate-drive-by" />
            </div>
        </section>
        </>
        )}


        {/*  SECCIÓN PROMOCIONAL DOBLE BLOQUE (ANIMACIÓN PREMIUM)  */}
        <section className="relative py-10 md:py-12 overflow-hidden bg-[#020202]">
            {/*  Fondo de la sección  */}
            <div className="absolute inset-0 z-0">
                <img src="assets/fondo2.png" alt="Promo Background" className="w-full h-full object-cover" />
            </div>

            {/*  Capa Decorativa: Carro PNG con animación sutil (Solo Desktop/Tablet)  */}
            <div className="absolute inset-x-0 bottom-0 top-0 z-[5] pointer-events-none overflow-hidden hidden md:block">
                <div className="absolute bottom-0 left-0 w-full animate-car-drift">
                    <img src="assets/carrosinfondo.png" alt="Decorative Car"
                        className="w-[600px] h-auto object-contain blur-[4px] opacity-30" />
                </div>
            </div>

            <div className="container-main relative z-10">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">

                    {/*  Bloque 1: Compra en Línea  */}
                    <div className="reveal-left promo-shine group">
                        <div
                            className="relative overflow-hidden rounded-xl border border-white/30 bg-black/40 backdrop-blur-[2px] p-8 md:p-12 flex flex-col items-center text-center transition-all duration-500 hover:border-white/60 hover:bg-black/60 hover:shadow-[0_0_40px_rgba(255,255,255,0.15)] shadow-2xl h-full">
                            <h2
                                className="text-2xl md:text-4xl font-black text-white mb-3 tracking-tight italic uppercase drop-shadow-lg">
                                Compra en Línea</h2>
                            <div className="w-16 h-[1.5px] bg-white/30 mb-6"></div>
                            <p className="text-gray-100 text-sm md:text-base mb-10 max-w-xs leading-relaxed drop-shadow">
                                Paga con tarjetas de forma rápida y segura.</p>

                            <div className="mt-auto w-full max-w-[300px]">
                                <button onClick={() => document.getElementById('participate')?.scrollIntoView({ behavior: 'smooth' })}
                                    className="btn-primary btn-glossy w-full">
                                    <span className="relative z-10">COMPRAR AHORA</span>
                                </button>
                            </div>
                        </div>
                    </div>

                    {/*  Bloque 2: Atención Personalizada  */}
                    <div className="reveal-right promo-shine delay-200 group">
                        <div
                            className="relative overflow-hidden rounded-xl border border-white/30 bg-black/40 backdrop-blur-[2px] p-8 md:p-12 flex flex-col items-center text-center transition-all duration-500 hover:border-white/60 hover:bg-black/60 hover:shadow-[0_0_40px_rgba(255,255,255,0.15)] shadow-2xl h-full">
                            <h2
                                className="text-2xl md:text-4xl font-black text-white mb-3 tracking-tight italic uppercase drop-shadow-lg">
                                Atención Personalizada</h2>
                            <div className="w-16 h-[1.5px] bg-white/30 mb-6"></div>
                            <p className="text-gray-100 text-sm md:text-base mb-10 max-w-xs leading-relaxed drop-shadow">
                                Habla con un asesor y resuelve tus dudas.</p>

                            <div className="mt-auto w-full max-w-[300px]">
                                <a 
                                    href={(!branding?.tipo_contacto || branding.tipo_contacto === 'whatsapp') 
                                        ? `https://wa.me/${(branding?.valor_contacto || '593939603823').replace(/[^0-9]/g, '')}` 
                                        : `https://ig.me/m/${branding?.valor_contacto || 'wishway.oficial'}`}
                                    target="_blank" rel="noopener noreferrer"
                                    className={`btn-primary btn-glossy w-full gap-3 ${
                                        (!branding?.tipo_contacto || branding.tipo_contacto === 'whatsapp')
                                            ? '!bg-[#25D366] !shadow-green-500/20'
                                            : '!bg-gradient-to-r !from-[#833AB4] !via-[#FD1D1D] !to-[#F56040] !shadow-red-500/20'
                                    }`}
                                >
                                    <div
                                        className="w-6 h-6 rounded-full border border-white/30 flex items-center justify-center bg-white/10 shrink-0">
                                        {(!branding?.tipo_contacto || branding.tipo_contacto === 'whatsapp') ? (
                                            <svg className="w-3 h-3 text-white fill-current" viewBox="0 0 24 24">
                                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                                            </svg>
                                        ) : (
                                            <svg className="w-3 h-3 text-white fill-current" viewBox="0 0 24 24">
                                                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c.796 0 1.441.645 1.441 1.44s-.645 1.44-1.441 1.44c-.795 0-1.439-.645-1.439-1.44s.644-1.44 1.439-1.44z" />
                                            </svg>
                                        )}
                                    </div>
                                    <span className="relative z-10 shrink-0 uppercase">
                                        {(!branding?.tipo_contacto || branding.tipo_contacto === 'whatsapp') ? 'HABLAR POR WHATSAPP' : 'MENSAJE POR INSTAGRAM'}
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </section>

        {/*  SECCIÓN DE URGENCIA (REPLICA EXACTA)  */}
        <section className="relative py-6 md:py-8 overflow-hidden bg-[#050505] border-y border-[#1a1a1a]">
            {/*  Glow de fondo  */}
            <div className="absolute inset-0 pointer-events-none">
                <div
                    className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,107,0,0.18),transparent_55%)]">
                </div>
                <div
                    className="absolute top-1/2 left-1/2 w-[500px] h-[120px] -translate-x-1/2 -translate-y-1/2 bg-[#FF6B00]/10 blur-3xl rounded-full animate-pulse">
                </div>
            </div>

            <div className="relative container-main max-w-5xl px-4 text-center">
                    <h3 className="text-lg sm:text-xl md:text-3xl font-black tracking-tight text-white">
                        Cupos limitados disponibles.
                        <div
                        className="inline-flex items-center gap-2 bg-[#FF6B00]/10 border border-[#FF6B00]/20 px-4 py-2 rounded-full font-black text-sm uppercase tracking-widest mx-auto lg:mx-0 shadow-[0_0_20px_rgba(255,107,0,0.15)] mb-6 animate-pulse">
                        <span className="w-2 h-2 rounded-full bg-[#FF6B00] shadow-[0_0_10px_#FF6B00]"></span>
                        <span className="text-[#FF6B00] drop-shadow-[0_0_10px_rgba(255,107,0,0.35)]">
                            {heroConfig?.progreso_falso || 35}% Vendido
                        </span>
                    </div>
                    </h3>
            </div>
        </section>

        {/*  SECCIÓN DE TESTIMONIOS (Oculta temporalmente)  */}
        <section id="testimonials" className="relative py-10 md:py-12 bg-gray-50 overflow-hidden border-t border-gray-100">
            <div className="max-w-5xl mx-auto px-6 relative z-10">
                {/*  Encabezado  */}
                <div className="text-center mb-8 md:mb-10 reveal">
                    <div className="flex items-center justify-center gap-3 mb-4">
                        <span className="w-10 md:w-16 h-[1.5px] bg-gray-300"></span>
                        <h2 className="text-3xl md:text-5xl font-black tracking-tighter text-[#1a2233] uppercase">
                            Nuestros <span className="text-[#f24e00]">Ganadores</span>
                        </h2>
                        <span className="w-10 md:w-16 h-[1.5px] bg-gray-300"></span>
                    </div>
                    <p className="text-gray-500 font-bold text-sm md:text-lg tracking-wide uppercase">
                        Casos reales de éxito
                    </p>
                </div>

                {/*  Grid de Videos  */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 reveal">
                    
                    {/*  Video 1  */}
                    <div className="bg-white p-4 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_50px_rgba(242,78,0,0.12)] transition-shadow duration-300 border border-gray-100 overflow-hidden flex flex-col">
                        <div className="rounded-xl overflow-hidden w-full relative">
                            {/*  INICIO CÓDIGO WISTIA VIDEO 1  */}
                             <wistia-player media-id="f45zq8md0i" aspect="0.5705229793977813"></wistia-player>
                            {/*  FIN CÓDIGO WISTIA VIDEO 1  */}
                        </div>
                    </div>

                    {/*  Video 2  */}
                    <div className="bg-white p-4 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_50px_rgba(242,78,0,0.12)] transition-shadow duration-300 border border-gray-100 overflow-hidden flex flex-col">
                        <div className="rounded-xl overflow-hidden w-full relative">
                            {/*  INICIO CÓDIGO WISTIA VIDEO 2  */}
                             <wistia-player media-id="vozquer2el" aspect="0.5705229793977813"></wistia-player>
                            {/*  FIN CÓDIGO WISTIA VIDEO 2  */}
                        </div>
                    </div>

                    {/*  Video 3  */}
                    <div className="bg-white p-4 rounded-2xl shadow-[0_15px_40px_rgba(0,0,0,0.08)] hover:shadow-[0_20px_50px_rgba(242,78,0,0.12)] transition-shadow duration-300 border border-gray-100 overflow-hidden flex flex-col">
                        <div className="rounded-xl overflow-hidden w-full relative">
                            {/*  INICIO CÓDIGO WISTIA VIDEO 3  */}
                             <wistia-player media-id="hmrb2x3jit" aspect="0.5705229793977813"></wistia-player>
                            {/*  FIN CÓDIGO WISTIA VIDEO 3  */}
                        </div>
                    </div>

                </div>
            </div>
        </section>



        {/*  SECCIÓN DE PREGUNTAS FRECUENTES (FAQ)  */}
        <section id="faq" className="relative py-20 overflow-hidden">
            {/*  Fondo con imagen y overlay para legibilidad  */}
            <div className="absolute inset-0 z-0">
                <img src="assets/fondosincarro.png" alt="Background" className="w-full h-full object-cover" />
                <div className="absolute inset-0 bg-black/40"></div>
            </div>

            <div className="container-main relative z-10 px-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-10 items-center">

                    {/*  Lado Izquierdo: FAQ  */}
                    <div className="reveal-left delay-100">
                        {/*  Título (Más pequeño para reducir altura total)  */}
                        <div className="mb-2 text-center lg:text-left">
                            <h2 className="text-2xl md:text-3xl font-black text-white tracking-tight">
                                Preguntas Frecuentes
                            </h2>
                        </div>
                        <div className="space-y-2">
                            {/*  Pregunta 1  */}
                            <div
                                className={`faq-item border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-all duration-300 ${openFaq === 1 ? 'open' : ''}`}>
                                <button onClick={() => setOpenFaq(openFaq === 1 ? null : 1)}
                                    className="w-full flex items-center justify-between p-3 md:p-4 text-left group bg-white hover:bg-gray-50 transition-colors">
                                    <span className="text-gray-900 font-bold text-sm md:text-base pr-6">¿Es seguro y
                                        transparente esta actividad?</span>
                                    <span className="faq-icon text-orange-500 shrink-0">
                                        <svg className="w-5 h-5 md:w-6 h-6" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"
                                                d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </span>
                                </button>
                                <div className={`faq-answer px-6 bg-white ${openFaq === 1 ? 'open' : ''}`}>
                                    <div
                                        className="text-gray-600 text-xs md:text-sm leading-relaxed py-2 border-t border-gray-100">
                                        Sí. Es una actividad 100% legal, transparente y verificable.<br />
                                        El resultado se define con la Lotería Nacional, garantizando que no puede ser
                                        manipulado.<br />
                                        Tus datos están protegidos y no se comparten.
                                    </div>
                                </div>
                            </div>

                            {/*  Pregunta 2  */}
                            <div
                                className={`faq-item border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-all duration-300 ${openFaq === 2 ? 'open' : ''}`}>
                                <button onClick={() => setOpenFaq(openFaq === 2 ? null : 2)}
                                    className="w-full flex items-center justify-between p-3 md:p-4 text-left group bg-white hover:bg-gray-50 transition-colors">
                                    <span className="text-gray-900 font-bold text-sm md:text-base pr-6">¿Cómo se elige al
                                        ganador?</span>
                                    <span className="faq-icon text-orange-500 shrink-0">
                                        <svg className="w-5 h-5 md:w-6 h-6" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"
                                                d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </span>
                                </button>
                                <div className={`faq-answer px-6 bg-white ${openFaq === 2 ? 'open' : ''}`}>
                                    <div
                                        className="text-gray-600 text-xs md:text-sm leading-relaxed py-2 border-t border-gray-100">
                                        Se basa en el resultado oficial de la Lotería Nacional.<br />
                                        Tu boleto participa con un número asignado y gana quien tenga el número ganador
                                        o el más cercano según la mecánica.
                                    </div>
                                </div>
                            </div>

                            {/*  Pregunta 3  */}
                            <div
                                className={`faq-item border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-all duration-300 ${openFaq === 3 ? 'open' : ''}`}>
                                <button onClick={() => setOpenFaq(openFaq === 3 ? null : 3)}
                                    className="w-full flex items-center justify-between p-3 md:p-4 text-left group bg-white hover:bg-gray-50 transition-colors">
                                    <span className="text-gray-900 font-bold text-sm md:text-base pr-6">¿Quiénes pueden
                                        participar?</span>
                                    <span className="faq-icon text-orange-500 shrink-0">
                                        <svg className="w-5 h-5 md:w-6 h-6" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"
                                                d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </span>
                                </button>
                                <div className={`faq-answer px-6 bg-white ${openFaq === 3 ? 'open' : ''}`}>
                                    <div
                                        className="text-gray-600 text-xs md:text-sm leading-relaxed py-2 border-t border-gray-100">
                                        Participa todo el Ecuador 🇪🇨<br />
                                        No importa tu ciudad. Si ganas, el premio se entrega sin problema.<br />
                                        La entrega del vehículo se realiza en Quito.
                                    </div>
                                </div>
                            </div>

                            {/*  Pregunta 4  */}
                            <div
                                className={`faq-item border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-all duration-300 ${openFaq === 4 ? 'open' : ''}`}>
                                <button onClick={() => setOpenFaq(openFaq === 4 ? null : 4)}
                                    className="w-full flex items-center justify-between p-3 md:p-4 text-left group bg-white hover:bg-gray-50 transition-colors">
                                    <span className="text-gray-900 font-bold text-sm md:text-base pr-6">¿Qué pasa si gano el
                                        BMW?</span>
                                    <span className="faq-icon text-orange-500 shrink-0">
                                        <svg className="w-5 h-5 md:w-6 h-6" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"
                                                d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </span>
                                </button>
                                <div className={`faq-answer px-6 bg-white ${openFaq === 4 ? 'open' : ''}`}>
                                    <div
                                        className="text-gray-600 text-xs md:text-sm leading-relaxed py-2 border-t border-gray-100">
                                        El ganador se anuncia en transmisión en vivo.<br />
                                        El vehículo se entrega con respaldo legal a través de la Notaría 4 de Quito,
                                        garantizando seguridad y transparencia.
                                    </div>
                                </div>
                            </div>

                            {/*  Pregunta 5  */}
                            <div
                                className={`faq-item border border-gray-200 rounded-xl overflow-hidden shadow-sm transition-all duration-300 ${openFaq === 5 ? 'open' : ''}`}>
                                <button onClick={() => setOpenFaq(openFaq === 5 ? null : 5)}
                                    className="w-full flex items-center justify-between p-3 md:p-4 text-left group bg-white hover:bg-gray-50 transition-colors">
                                    <span className="text-gray-900 font-bold text-sm md:text-base pr-6">¿Cómo compro y cómo
                                        recibo mis boletos?</span>
                                    <span className="faq-icon text-orange-500 shrink-0">
                                        <svg className="w-5 h-5 md:w-6 h-6" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5"
                                                d="M19 9l-7 7-7-7"></path>
                                        </svg>
                                    </span>
                                </button>
                                <div className={`faq-answer px-6 bg-white ${openFaq === 5 ? 'open' : ''}`}>
                                    <div
                                        className="text-gray-600 text-xs md:text-sm leading-relaxed py-2 border-t border-gray-100">
                                        Puedes pagar mediante:<br />
                                        • 💳 Tarjeta<br />
                                        • 💸 Transferencia bancaria<br /><br />
                                        Una vez realizado el pago, recibirás acceso a tu cuenta donde podrás ver tus
                                        boletos asignados de forma privada con tu usuario y contraseña.
                                    </div>
                                </div>
                            </div>


                        </div>
                    </div>

                    {/*  Lado Derecho: El Carro (Desktop)  */}
                    <div className="hidden lg:flex justify-end reveal-car-scale">
                        <img src="assets/carrosinfondo.png" alt="BMW Ganador"
                            className="max-w-[130%] h-auto drop-shadow-[0_40px_100px_rgba(255,107,0,0.35)] pointer-events-none transform translate-x-12 scale-100 transition-all duration-700" />
                    </div>
                </div>

                {/*  Carro en Móvil / Tablet (Positioned below)  */}
                <div className="lg:hidden mt-4 flex justify-center reveal-car-scale">
                    <img src="assets/carrosinfondo.png" alt="BMW Ganador"
                        className="max-w-[110%] h-auto drop-shadow-[0_20px_50px_rgba(255,107,0,0.25)] scale-95" />
                </div>
            </div>
        </section>

        {/*  SECCIÓN FINAL CTA (CALL TO ACTION)  */}
        <section id="final-cta" className="relative py-20 bg-white overflow-hidden border-t border-gray-100">
            <div className="container-main relative z-10 px-6 text-center">
                {/*  Texto de Urgencia  */}
                <div className="reveal-up mb-8">
                    <h2
                        className="text-2xl md:text-5xl lg:text-5xl font-black text-gray-900 tracking-tight leading-tight md:whitespace-nowrap">
                        Participa ahora antes de que <span className="text-brand-primary">se complete</span>
                    </h2>
                </div>

                {/*  Barra de Progreso Final Réplica Exacta  */}
                <div className="max-w-4xl mx-auto reveal-up delay-200 px-4 md:px-0">
                    <div
                        className="relative h-10 md:h-12 w-full bg-gray-200 rounded-lg overflow-hidden border border-gray-300 shadow-inner group">

                        {/*  Franja Anaranjada Glosssy con Llenado Animado (Forzado con Style para evitar fallos CSS)  */}
                        <div className="absolute inset-y-0 left-0 bg-gradient-to-b from-[#ffb447] via-[#ff8a00] to-[#e65c00] rounded-r-md overflow-hidden shadow-[inset_0_1px_1px_rgba(255,255,255,0.6),0_2px_4px_rgba(0,0,0,0.2)] z-10"
                            style={{ width: '65%', animation: 'fill-65 2.5s cubic-bezier(0.19, 1, 0.22, 1) forwards' }}>

                            {/*  Animación Especial: Energy Pulse  */}
                            <div
                                className="absolute inset-0 bg-white/20 animate-pulse-slow opacity-0 group-hover:opacity-100 transition-opacity">
                            </div>

                            {/*  Efecto Shimmer de Cristal  */}
                            <div
                                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent -skew-x-[45deg] animate-shine pointer-events-none w-[150%]">
                            </div>

                            {/*  Highlight Superior (3D Effect)  */}
                            <div
                                className="absolute top-0 left-0 w-full h-[40%] bg-gradient-to-b from-white/30 to-transparent">
                            </div>

                            {/*  Texto del Porcentaje (Réplica Exacta)  */}
                            <div className="absolute inset-0 flex items-center justify-center z-20">
                                <p className="text-[10px] md:text-[13px] font-black uppercase tracking-[0.35em] text-[#000] drop-shadow-[0_1px_0_rgba(255,255,255,0.3)] pointer-events-none">
                                    {heroConfig?.progreso_falso || 35}% <span className="font-bold">Vendido</span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/*  MODAL LIGHTBOX (FULL SIZE IMAGE)  */}
        <div id="lightbox"
            className="fixed inset-0 z-[100] hidden bg-black/95 backdrop-blur-sm flex items-center justify-center p-4 md:p-10 cursor-zoom-out transition-all duration-500 opacity-0 pointer-events-none">
            <button id="close-lightbox"
                className="absolute top-6 right-6 text-white/50 hover:text-white transition-colors z-[110]">
                <svg className="w-10 h-10" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 18L18 6M6 6l12 12">
                    </path>
                </svg>
            </button>
            <div className="relative max-w-5xl w-full h-full flex items-center justify-center">
                <img id="lightbox-img" src="" alt="Full Size"
                    className="max-w-full max-h-full rounded-lg shadow-2xl transition-transform duration-500 scale-90 object-contain" />
            </div>
        </div>

        {/*  SECCIÓN DE REDES SOCIALES (PREMIUM UPGRADE)  */}
        <section className="bg-[#020202] py-14 border-t border-white/5 relative overflow-hidden group/sec">
            {/*  Sutil halo dorado de fondo  */}
            <div
                className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(191,149,63,0.08),transparent_70%)] opacity-0 group-hover/sec:opacity-100 transition-opacity duration-1000">
            </div>

            <div
                className="container-main relative z-10 flex flex-col md:flex-row items-center justify-center gap-8 md:gap-20">
                {/*  Texto con espaciado animado  */}
                <p
                    className="text-gray-500 text-[10px] md:text-xs font-black tracking-[0.4em] uppercase transition-all duration-700 group-hover/sec:tracking-[0.6em] group-hover/sec:text-gray-400">
                    Síguenos en nuestras redes
                </p>

                <div className="flex items-center gap-10 md:gap-14">
                    {/*  Facebook  */}
                    <a href="https://www.facebook.com/share/1D5pbH43bz/?mibextid=wwXIfr" target="_blank"
                        rel="noopener noreferrer"
                        className="group relative text-gray-500 transition-all duration-500 hover:text-[#BF953F]">
                        {/*  Halo Dorado (Efecto Pro)  */}
                        <div
                            className="absolute inset-0 -m-6 bg-[#BF953F]/15 blur-2xl rounded-full scale-0 group-hover:scale-100 transition-transform duration-700 opacity-0 group-hover:opacity-100">
                        </div>

                        {/*  Icono con Rotación 3D  */}
                        <div
                            className="relative z-10 transform transition-all duration-500 group-hover:scale-125 group-hover:rotate-[12deg] drop-shadow-[0_0_10px_rgba(191,149,63,0)] group-hover:drop-shadow-[0_0_20px_rgba(191,149,63,0.4)]">
                            <svg className="w-6 h-6 md:w-8 md:h-8 fill-current" viewBox="0 0 24 24">
                                <path
                                    d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                            </svg>
                        </div>
                    </a>

                    {/*  Instagram  */}
                    <a href="https://www.instagram.com/wishway.oficial?igsh=YTl1bGw2amFkMWJq" target="_blank"
                        rel="noopener noreferrer"
                        className="group relative text-gray-500 transition-all duration-500 hover:text-[#BF953F]">
                        <div
                            className="absolute inset-0 -m-6 bg-[#BF953F]/15 blur-2xl rounded-full scale-0 group-hover:scale-100 transition-transform duration-700 opacity-0 group-hover:opacity-100">
                        </div>
                        <div
                            className="relative z-10 transform transition-all duration-500 group-hover:scale-125 group-hover:-rotate-[12deg] drop-shadow-[0_0_10px_rgba(191,149,63,0)] group-hover:drop-shadow-[0_0_20px_rgba(191,149,63,0.4)]">
                            <svg className="w-6 h-6 md:w-8 md:h-8 fill-current" viewBox="0 0 24 24">
                                <path
                                    d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c.796 0 1.441.645 1.441 1.44s-.645 1.44-1.441 1.44c-.795 0-1.439-.645-1.439-1.44s.644-1.44 1.439-1.44z" />
                            </svg>
                        </div>
                    </a>

                    {/*  TikTok  */}
                    <a href="https://www.tiktok.com/@wish.way.ec?_r=1&_d=d5a5kab21d007g&sec_uid=MS4wLjABAAAAWyuz02zadRLQsKSzEWwyu6I28pwCRUX8Y2gT9fMFFYyEKi8uSeUgHLO4MML3-cCp&share_author_id=7598291572153271304&sharer_language=es&source=h5_m&u_code=f1ge3f3ke938l8&item_author_type=1&utm_source=copy&tt_from=copy&enable_checksum=1&utm_medium=ios&share_link_id=E4417805-D1EB-4B0A-80F5-AC9911F725CA&user_id=7598291572153271304&sec_user_id=MS4wLjABAAAAWyuz02zadRLQsKSzEWwyu6I28pwCRUX8Y2gT9fMFFYyEKi8uSeUgHLO4MML3-cCp&social_share_type=4&ug_btm=b8727,b0&utm_campaign=client_share&share_app_id=1233"
                        target="_blank" rel="noopener noreferrer"
                        className="group relative text-gray-400 transition-all duration-500 hover:text-[#BF953F]">
                        <div
                            className="absolute inset-0 -m-6 bg-[#BF953F]/15 blur-2xl rounded-full scale-0 group-hover:scale-100 transition-transform duration-700 opacity-0 group-hover:opacity-100">
                        </div>
                        <div
                            className="relative z-10 transform transition-all duration-500 group-hover:scale-125 group-hover:rotate-[12deg] drop-shadow-[0_0_10px_rgba(191,149,63,0)] group-hover:drop-shadow-[0_0_20px_rgba(191,149,63,0.4)]">
                            <svg className="w-6 h-6 md:w-8 md:h-8 fill-current" viewBox="0 0 24 24">
                                <path
                                    d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
                            </svg>
                        </div>
                    </a>
                </div>
            </div>
        </section>

        {/*  Footer Simple  */}
        <footer className="bg-black py-8 border-t border-white/5">
            <div className="container-main text-center">
                <p className="text-gray-500 text-sm">&copy; <span id="current-year">2026</span> Wishway. Todos los derechos
                    reservados.</p>
            </div>
        </footer>

        {/*  Scripts  */}
        
</main>
</div>

      <SocialProof />

      <FastCheckoutModal 
        isOpen={isFastCheckoutOpen}
        onClose={() => setIsFastCheckoutOpen(false)}
        sorteo={sorteoActivo}
        paqueteId={selectedPackageId}
        paqueteData={selectedPackageData}
      />
    </>
  );
};

export default HomeComplete;
